import jsPDF from 'jspdf';

export interface CompanyBranding {
  company_name?: string;
  company_logo_url?: string;
  company_phone?: string;
  company_email?: string;
  company_address?: string;
}

/**
 * Fetch an image URL and convert to base64 data URL for jsPDF embedding.
 * Returns null if the fetch fails.
 */
export async function loadLogoImage(url: string): Promise<string | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------------------------
// THE MANCHESTER LOOK, for the investment-analysis PDF - his ruling 2026-09-09: "for that
// Manchester, that's going to be for the investment analysis, right? Which is what I wanted."
//
// Measured off the Cowork-built "Manchester Plaza Inglewood - 300 W Manchester Blvd - Investment
// Analysis (All-Cash)" (its .docx and its printed PDF, 2026-09-08): Calibri; red #C00000 for the
// masthead bar, the title, the verdict box and the rules; navy #1A2E47 for the deal name, the
// section titles and every table header row; #F2F2F2 tiles and zebra rows; #FBEDED behind a NO GO;
// an 18pt red bar on every page with the firm at left and the date / structure at right; on page 1
// the Apex and KW marks under the bar with the firm line and "Prepared for ... · Confidential
// Investment Analysis" at right; a footer line with both brokers and the page number. The verdict
// colours are the four-state table in docs/INVESTMENT-ANALYSIS-HOUSE-STYLE.md, which the specimen
// prints. The same palette drives the brochure builder's `manchester` theme on the backend.
// ---------------------------------------------------------------------------------------------

type RGB = [number, number, number];

export const MANCHESTER_PDF = {
  red: [192, 0, 0] as RGB,          // #C00000
  navy: [26, 46, 71] as RGB,        // #1A2E47
  ink: [34, 34, 34] as RGB,         // #222222
  body: [51, 51, 51] as RGB,        // #333333
  muted: [89, 89, 89] as RGB,       // #595959
  tile: [242, 242, 242] as RGB,     // #F2F2F2
  calloutFill: [251, 237, 237] as RGB, // #FBEDED
  rule: [191, 191, 191] as RGB,     // #BFBFBF
  white: [255, 255, 255] as RGB,
  green: [46, 125, 50] as RGB,      // #2E7D32 - GO
  greenFill: [234, 245, 236] as RGB, // #EAF5EC
  amber: [199, 134, 11] as RGB,     // #C7860B - CONDITIONAL / CAUTION
  amberFill: [251, 243, 226] as RGB, // #FBF3E2
  /** The bar across the top of every page, in mm (18pt). */
  barHeight: 6.35,
  firmLine: 'APEX REAL ESTATE SERVICES  /  KW COMMERCIAL',
} as const;

/** The family jsPDF knows the embedded face by. */
export const ANALYSIS_FONT = 'Carlito';
export type AnalysisFace = 'normal' | 'bold' | 'italic';

/**
 * Registers Carlito (regular, bold, italic) with this document and returns the family name to
 * `setFont` with. The faces live in `carlitoFonts.ts`, 2.6 MB of base64, loaded on first use so
 * the app shell never carries them. Every `setFont` in the analysis export goes through the
 * returned name - Helvetica is what the export used to print in, and is now only the fallback
 * jsPDF would take if a face were missing.
 */
export async function registerAnalysisFont(doc: jsPDF): Promise<string> {
  const { CARLITO_FACES } = await import('./carlitoFonts');
  for (const f of CARLITO_FACES) {
    doc.addFileToVFS(f.file, f.base64);
    doc.addFont(f.file, ANALYSIS_FONT, f.style);
  }
  return ANALYSIS_FONT;
}

export interface PageFrame { pageWidth: number; pageHeight: number; margin: number }

/**
 * The masthead: the red bar across the top of every page, the firm at left, the date and the
 * structure ("September 8, 2026 - All-Cash (1031)") at right. Returns the bar's height.
 */
export function drawMasthead(doc: jsPDF, family: string, frame: PageFrame, firmName: string, right: string): number {
  const c = MANCHESTER_PDF;
  doc.setFillColor(...c.red);
  doc.rect(0, 0, frame.pageWidth, c.barHeight, 'F');
  doc.setTextColor(...c.white);
  doc.setFontSize(9);
  doc.setFont(family, 'bold');
  doc.text(firmName, frame.margin, c.barHeight - 2);
  doc.setFont(family, 'normal');
  doc.setFontSize(8.5);
  doc.text(right, frame.pageWidth - frame.margin, c.barHeight - 2, { align: 'right' });
  return c.barHeight;
}

/**
 * The logo row under the masthead - page 1 only, as the specimen's: the Apex mark and the KW mark
 * at left, the firm line and the document's line ("Prepared for ... · Confidential Investment
 * Analysis") at right. Returns the y below the row. A mark that failed to load is simply absent.
 */
export function drawLogoRow(
  doc: jsPDF,
  family: string,
  frame: PageFrame,
  y: number,
  marks: { apex?: string | null; kw?: string | null },
  subLine: string
): number {
  const c = MANCHESTER_PDF;
  // The marks at their own proportions: the Apex mark is 1080x690, the KW box 275x181.
  const h = 9;
  let x = frame.margin;
  if (marks.apex) {
    try { doc.addImage(marks.apex, 'AUTO', x, y, h * (1080 / 690), h); x += h * (1080 / 690) + 4; } catch { /* decorative */ }
  }
  if (marks.kw) {
    const kh = 7.4;
    try { doc.addImage(marks.kw, 'AUTO', x, y + (h - kh) / 2, kh * (275 / 181), kh); } catch { /* decorative */ }
  }
  doc.setFontSize(7);
  doc.setFont(family, 'bold');
  doc.setTextColor(...c.muted);
  doc.text(c.firmLine, frame.pageWidth - frame.margin, y + 3.2, { align: 'right' });
  doc.setFont(family, 'normal');
  doc.setTextColor(...c.body);
  doc.text(subLine, frame.pageWidth - frame.margin, y + 6.8, { align: 'right' });
  return y + h + 4;
}

/** The footer line: a hairline, the firm | brokers | Confidential at left, "Page N" at right. */
export function drawFooterLine(doc: jsPDF, family: string, frame: PageFrame, left: string, page: number): void {
  const c = MANCHESTER_PDF;
  doc.setDrawColor(...c.rule);
  doc.setLineWidth(0.3);
  doc.line(frame.margin, frame.pageHeight - 16, frame.pageWidth - frame.margin, frame.pageHeight - 16);
  doc.setFontSize(7);
  doc.setFont(family, 'normal');
  doc.setTextColor(...c.muted);
  doc.text(left, frame.margin, frame.pageHeight - 11);
  doc.text(`Page ${page}`, frame.pageWidth - frame.margin, frame.pageHeight - 11, { align: 'right' });
}

/** A section title: navy, bold, 14pt, over a red rule - "PROPERTY OVERVIEW". Returns the y below it. */
export function drawAnalysisSectionTitle(doc: jsPDF, family: string, frame: PageFrame, title: string, y: number): number {
  const c = MANCHESTER_PDF;
  doc.setTextColor(...c.navy);
  doc.setFontSize(13);
  doc.setFont(family, 'bold');
  doc.text(title, frame.margin, y);
  y += 2.2;
  doc.setDrawColor(...c.red);
  doc.setLineWidth(0.5);
  doc.line(frame.margin, y, frame.pageWidth - frame.margin, y);
  return y + 6;
}

/** The table styles every analysis table shares: the family, a navy head, zebra rows in the tile grey. */
export function analysisTableStyles(family: string) {
  const c = MANCHESTER_PDF;
  return {
    styles: { font: family, textColor: c.ink, lineWidth: 0 },
    headStyles: { font: family, fillColor: c.navy, textColor: c.white, fontStyle: 'bold' as const },
    alternateRowStyles: { fillColor: c.tile },
  };
}

/**
 * The two brokers, for the PREPARED BY block. ⚠️ A second copy of what `brochureTemplate.ts`
 * holds on the backend (ROBERT / JOSEPH / FIRM_ADDRESS) - the frontend cannot import it. Kept in
 * step by hand; the firm line is his ruling of 2026-10-04 (AIW1-F5): Redlands for every new document, superseding
 * "Suite A, 92544" (2026-09-09).
 */
export const PREPARED_BY = [
  { name: 'Robert Mendieta, Jr., CCIM', title: 'Associate Broker', dre: 'CA DRE Broker License #01422904', phone: '(951) 977-3251', email: 'robert@apex-res.com' },
  { name: 'Joseph Lombera', title: 'Commercial Agent', dre: 'CA DRE License #01971957', phone: '(909) 406-6538', email: 'joseph@apex-res.com' },
] as const;
export const FIRM_ADDRESS_LINE = '1473 Ford St #200, Redlands, CA 92373';

/**
 * ⭐ The export's file name. His question 2026-09-09: "you name all your files the same, shouldn't we
 * name them differently so we can track changes?" Every export used to be
 * `<Kind>_<address>_<YYYY-MM-DD>.pdf`, so a second export of the same deal on the same day carried the
 * SAME name and either overwrote the first or landed beside it as "(1)". The minute is in the name now
 * and the kind reads as words: "Investment Analysis - 13368 Main Street - 2026-09-09 1432.pdf".
 * ⚠️ The name is the only record there is: the app keeps no copy of an exported PDF (backlog LB9).
 */
export function exportFileName(kind: string, address: string | null | undefined, when: Date = new Date()): string {
  const where = (address || 'Property').replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim();
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp = `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())} ${pad(when.getHours())}${pad(when.getMinutes())}`;
  return `${kind} - ${where} - ${stamp}.pdf`;
}
